import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import PhotoBook5 from "../assets/img/PhotoBook-8x8.jpg";
import CartService from '../Services/CartService';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../App/Account/ConfirmModal';
import editorStore from '../stores/EditorStore';

interface CartModalProps {
  cartInputs: {
    projectName: string;
    productBasePrice: number;
    productId: number;
    canvasId: number;
    productType: string;
  };
  onClose: () => void;
}

const C = {
  overlay:      'rgba(11,27,63,0.45)',
  panel:        '#FFFFFF',
  surface:      '#F3F7FF',
  border:       '#E5E7EB',
  divider:      '#E5E7EB',
  text1:        '#0B1B3F',
  text2:        '#475569',
  text3:        '#64748B',
  accent:       '#0B5FFF',
  accentHover:  '#0847C7',
  stepper:      '#9CA3AF',
  stepperHover: '#6B7280',
};

const labelStyle: React.CSSProperties = { fontSize: 14, fontWeight: 500, color: C.text1, marginBottom: 10 };

const CartModal: React.FC<CartModalProps> = ({ cartInputs, onClose }) => {
  const [quantity, setQuantity]               = useState(1);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [cartID, setCartID]                   = useState<number | null>(null);
  const [createdQuantity, setCreatedQuantity] = useState(1);
  const [associatedProdId, setAssociatedProdId] = useState(0);
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [loading, setLoading]                 = useState(false);
  const navigate = useNavigate();

  const handleIncrement = () => setQuantity(prev => prev + 1);
  const handleDecrement = () => setQuantity(prev => (prev > 1 ? prev - 1 : 1));

  useEffect(() => {
    const loadImageURL = async () => {
      const currentPageId = editorStore.activePageId;
      const currentImage  = await editorStore.toDataURL({ pageId: currentPageId });
      setCurrentImageUrl(currentImage);
    };
    loadImageURL();
  }, []);

  const ProceedToAddCart = async (canvasId: number, qty: number) => {
    const postData = { cartID: 0, associatedProductID: canvasId, quantity: qty };
    const result   = await CartService.CreateCart(cartInputs.productId, postData);
    if (!result || result === 'Failed to cart the product') {
      toast.error('Failed to add product to cart.', { position: 'top-center' });
      return null;
    }
    if (!result.cartExists) {
      toast.success('Product successfully added to the cart.', { position: 'top-center' });
      navigate('/yourcart', { state: { canvasId }, replace: false });
    }
    return result;
  };

  const handleAddToCart = async () => {
    setLoading(true);
    const result = await ProceedToAddCart(cartInputs.canvasId, quantity);
    setLoading(false);
    if (result?.cartExists === true) {
      setCreatedQuantity(result.quantity);
      setCartID(result.cartID);
      setAssociatedProdId(result.associatedProductID);
      setShowConfirmation(true);
    }
  };

  const handleConfirmAddMore = async () => {
    if (cartID) {
      const updateData = { cartID, associatedProductID: associatedProdId, quantity: createdQuantity + 1 };
      const result = await CartService.UpdateCart(cartInputs.productId, cartInputs.canvasId, updateData);
      if (result) {
        toast.success('Quantity updated successfully.', { position: 'top-center' });
        setShowConfirmation(false);
        navigate('/yourcart', { state: { canvasID: cartInputs.canvasId }, replace: false });
      }
    } else {
      toast.error('Unable to update cart. Cart ID is missing.', { position: 'top-center' });
    }
  };

  const totalPrice = (cartInputs.productBasePrice * quantity).toFixed(2);

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: C.overlay, zIndex: 50 }}
      />

      {/* Panel */}
      <div
        className="font-jakarta antialiased"
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: '380px',
          background: C.panel,
          zIndex: 51,
          display: 'flex', flexDirection: 'column',
          boxShadow: '-8px 0 32px rgba(11,27,63,0.15)',
        }}
      >

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 24, fontWeight: 500, color: C.text1 }}>
            Add to Cart
          </span>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 30, height: 30,
              background: 'none', border: 'none',
              color: C.text3, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = C.text1; }}
            onMouseLeave={e => { e.currentTarget.style.color = C.text3; }}
          >
            <XMarkIcon style={{ width: 22, height: 22 }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 24px 24px' }}>

          {/* Preview */}
          <div style={{ marginBottom: 24 }}>
            <div style={labelStyle}>Preview</div>
            <div style={{
              padding: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              minHeight: 140,
            }}>
              {cartInputs.productType === 'canvas' ? (
                <img
                  src={currentImageUrl || undefined}
                  alt="Preview"
                  style={{
                    maxWidth: '100%', maxHeight: 160,
                    objectFit: 'contain',
                    borderRadius: 4,
                    boxShadow: '0 4px 16px rgba(11,27,63,0.15)',
                  }}
                />
              ) : (
                <img
                  src={PhotoBook5}
                  alt="Preview"
                  style={{
                    maxWidth: '100%', maxHeight: 160,
                    objectFit: 'contain',
                    borderRadius: 4,
                    boxShadow: '0 4px 16px rgba(11,27,63,0.15)',
                  }}
                />
              )}
            </div>
          </div>

          {/* Project name */}
          <div style={{ marginBottom: 24 }}>
            <div style={labelStyle}>Project Name</div>
            <div style={{
              padding: '9px 12px',
              border: `1px solid ${C.border}`,
              borderRadius: 4,
              fontSize: 14, color: C.text2,
            }}>
              {cartInputs.projectName}
            </div>
          </div>

          {/* Quantity */}
          <div style={{ marginBottom: 24 }}>
            <div style={labelStyle}>Quantity</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button
                onClick={handleDecrement}
                aria-label="Decrease quantity"
                style={{
                  width: 32, height: 32, borderRadius: 4,
                  background: C.stepper, border: 'none',
                  color: '#fff', fontSize: 20, lineHeight: 1,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = C.stepperHover; }}
                onMouseLeave={e => { e.currentTarget.style.background = C.stepper; }}
              >
                −
              </button>
              <div style={{
                width: 48, height: 32,
                color: C.text1, fontSize: 15,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {quantity}
              </div>
              <button
                onClick={handleIncrement}
                aria-label="Increase quantity"
                style={{
                  width: 32, height: 32, borderRadius: 4,
                  background: C.stepper, border: 'none',
                  color: '#fff', fontSize: 20, lineHeight: 1,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = C.stepperHover; }}
                onMouseLeave={e => { e.currentTarget.style.background = C.stepper; }}
              >
                +
              </button>
            </div>
          </div>

          {/* Price */}
          <div style={{ fontSize: 22, fontWeight: 700, color: C.text1 }}>
            ${totalPrice}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: `1px solid ${C.divider}`,
          display: 'flex', gap: 16,
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, height: 44,
              background: '#fff',
              border: `1px solid ${C.stepper}`,
              borderRadius: 999,
              color: C.text1, fontSize: 14, fontWeight: 700,
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = C.surface; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
          >
            Cancel
          </button>
          <button
            onClick={handleAddToCart}
            disabled={loading}
            style={{
              flex: 1, height: 44,
              background: C.accent,
              border: 'none',
              borderRadius: 999,
              color: '#fff', fontSize: 14, fontWeight: 700,
              textTransform: 'uppercase',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = C.accentHover; }}
            onMouseLeave={e => { if (!loading) e.currentTarget.style.background = C.accent; }}
          >
            {loading ? 'Adding…' : 'Add to Cart'}
          </button>
        </div>
      </div>

      <ConfirmModal
        show={showConfirmation}
        message="Product already exists in your cart. Do you want to add one more quantity?"
        onConfirm={handleConfirmAddMore}
        onCancel={() => setShowConfirmation(false)}
      />
    </>
  );
};

export default CartModal;
